import {
  AnalyticsResponse,
  FilterType,
  HttpCallDetailsType,
  HttpRequest,
  InspectionViewRequest,
  InspectionViewRequestDetails,
  MessagingInspectionViewRequest,
  StateChangesDetailsType,
  StateChangesType,
  TxiDetails,
  TxiViewRequest,
} from 'types/rest/analyticsTypes';

/**
 * Analytics Data state stores all data related to analytics. The reason why it is kept separate is that we
 * do not need to sync data with local storage. They can be re-fetched any time from backend.
 */
export interface AnalyticsDataState {
  /** Data for all tabs */
  tabData: TabDataState[];

  /** Data of the current tab in focus */
  activeDataContext: TabDataState;
}

/* Concatenation of the MS & Reporting state */
export type TabDataState = MsAnalyticsDataState | any;

/**
 * Analytics data for Virtual Service view.
 */
export type MsAnalyticsDataState = {
  tabId: string | number;
  inspection: InspectionDataState;
  reporting: ReportsDataState;
  sessionTracking: any; // TODO - add when needed
  webhook: HttpDataState;
  httpCall: HttpDataState;
  stateChanges: StateChangesState;
};

export type InspectionDataState = {
  /** Boolean indicating if loading of requests is in progress */
  isLoadingRequests: boolean;

  /** Timestamp of Last refresh of the data */
  requestsLoadedTs: number | null;

  /** List of request for inspection view */
  requests: InspectionViewRequest[] | TxiViewRequest[] | MessagingInspectionViewRequest[];

  /** Boolean indicating loading of the request details */
  isLoadingRequestDetails: boolean;

  /**
   * Boolean indicating whether the new request is outdated (missing on the server)
   */
  isRequestOutdated: boolean;

  /**
   * Boolean indicating there was a request sent
   */
  isRequestSent: boolean;

  /** Request detail object */
  requestDetails: InspectionViewRequestDetails | TxiDetails | null;
};

/**
 * State Changes state.
 */
export type StateChangesState = {
  /** Boolean indicating if loading of stateChanges is in progress */
  isLoadingData: boolean;

  /** Timestamp of Last refresh of the data */
  lastRefreshTs: number | null;

  /** List of stateChanges for stateChanges view */
  stateChangesTypes: StateChangesType[];

  /** Boolean indicating loading of the stateChanges details */
  isLoadingDetails: boolean;

  /**
   * Boolean indicating whether the stateChanges is outdated (missing on the server)
   */
  isOutdated: boolean;

  /**
   * Boolean indicating there was a detail request sent
   */
  isDetailRequested: boolean;

  /** Detail */
  detail: StateChangesDetailsType[] | null;

  /** Filters */
  filters: FilterType[] | null;

  /** Changed Rows Count */
  changedRowsCount: number;
};

/**
 * Http / Webhook state.
 */
export type HttpDataState = {
  /** Boolean indicating if loading of webhooks is in progress */
  isLoadingData: boolean;

  /** Timestamp of Last refresh of the data */
  lastRefreshTs: number | null;

  /** List of request for Webhooks / Http Call view */
  httpRequests: HttpRequest[];

  /** Boolean indicating loading of the Http call / Webhook details */
  isLoadingDetails: boolean;

  /**
   * Boolean indicating whether the Webhook / Http call is outdated (missing on the server)
   */
  isOutdated: boolean;

  /**
   * Boolean indicating there was a detail request sent
   */
  isDetailRequested: boolean;

  /** Detail */
  detail: HttpCallDetailsType | null;
};

export type ReportsDataState = {
  isLoadingData: boolean;
  initialFetch: boolean;
  reportsData: ReportsData[];
};

export type ReportsData = {
  /** Boolean indicating if loading of data is in progress */
  isLoading: boolean;

  /** Time of the last reload of the data */
  lastReloadTs: number | null;

  /** Report which data belong in */
  reportType: string;

  /** Raw data */
  rawData: AnalyticsResponse | any;
};

export enum BlazeMetricTypes {
  //Daily counts
  TRANSACTIONS_TOTAL_COUNT = 'TRANSACTIONS_TOTAL_COUNT',

  //hit and misses
  TRANSACTIONS_MISSES_COUNT = 'TRANSACTIONS_MISSES_COUNT',
  TRANSACTIONS_HITS_COUNT = 'TRANSACTIONS_HITS_COUNT',
  TRANSACTIONS_SPECIFIC_HITS_COUNT = 'TRANSACTIONS_SPECIFIC_HITS_COUNT',

  //response time
  RESPONSE_TIME_MIN = 'RESPONSE_TIME_MIN',
  RESPONSE_TIME_MAX = 'RESPONSE_TIME_MAX',
  RESPONSE_TIME_VALUE = 'RESPONSE_TIME_VALUE',

  //transaction per second
  TRANSACTIONS_SECONDS_MIN = 'TRANSACTIONS_SECOND_MIN',
  TRANSACTIONS_SECONDS_MAX = 'TRANSACTIONS_SECOND_MAX',
  TRANSACTIONS_SECONDS_VALUE = 'TRANSACTIONS_SECOND_VALUE',

  //force delay
  FORCED_DELAY_MIN = 'FORCED_DELAY_MIN',
  FORCED_DELAY_MAX = 'FORCED_DELAY_MAX',
  FORCED_DELAY_VALUE = 'FORCED_DELAY_VALUE',

  //total hits transactional ms
  SERVICE_MOCK_TRANSACTIONS_TOTAL_COUNT = 'SERVICE_MOCK_TRANSACTIONS_TOTAL_COUNT',
}
